import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import toast from 'react-hot-toast';

const GitStatus = () => {
    const [gitStatus, setGitStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);

    const fetchGitStatus = async () => {
        try {
            setLoading(true);
            const response = await axios.get(getApiUrl('/api/git/status'));
            setGitStatus(response.data);
        } catch (error) {
            console.error('Error fetching Git status:', error);
            toast.error('Failed to fetch Git status');
        } finally {
            setLoading(false);
        }
    };

    const handleManualUpdate = async () => {
        try {
            setUpdating(true);
            const response = await axios.post(getApiUrl('/api/git/update'));
            
            if (response.data.success) {
                toast.success('Repository updated successfully');
                fetchGitStatus(); // Refresh status
            } else {
                toast.error(response.data.message || 'Failed to update repository');
            }
        } catch (error) {
            console.error('Error updating repository:', error);
            toast.error('Failed to update repository');
        } finally {
            setUpdating(false);
        }
    };

    useEffect(() => {
        fetchGitStatus();
        
        // Refresh status every 30 seconds
        const interval = setInterval(fetchGitStatus, 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className="card shadow-sm mb-3">
                <div className="card-body">
                    <div className="d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <span>Loading Git status...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!gitStatus) {
        return null;
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (exists) => {
        return exists ? 'success' : 'danger';
    };

    const getStatusIcon = (exists) => {
        return exists ? 'bi-check-circle-fill' : 'bi-x-circle-fill';
    };

    return (
        <div className="card shadow-sm mb-3">
            <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                        <i className="bi bi-git me-2"></i>
                        Git Repository Status
                    </h6>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={handleManualUpdate}
                        disabled={updating}
                    >
                        {updating ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-1" role="status">
                                    <span className="visually-hidden">Updating...</span>
                                </div>
                                Updating...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Update Now
                            </>
                        )}
                    </button>
                </div>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3">
                            <strong>Status:</strong>
                            <span className={`ms-2 text-${getStatusColor(gitStatus.exists)}`}>
                                <i className={`bi ${getStatusIcon(gitStatus.exists)} me-1`}></i>
                                {gitStatus.exists ? 'Repository Available' : 'Repository Not Found'}
                            </span>
                        </div>
                        
                        {gitStatus.repoUrl && (
                            <div className="mb-2">
                                <strong>Repository:</strong>
                                <span className="ms-2 text-primary">{gitStatus.repoUrl}</span>
                            </div>
                        )}
                        
                        {gitStatus.branch && (
                            <div className="mb-2">
                                <strong>Branch:</strong>
                                <span className="ms-2 badge bg-info">{gitStatus.branch}</span>
                            </div>
                        )}
                        
                        {gitStatus.currentBranch && (
                            <div className="mb-2">
                                <strong>Current Branch:</strong>
                                <span className="ms-2 badge bg-secondary">{gitStatus.currentBranch}</span>
                            </div>
                        )}
                    </div>
                    
                    <div className="col-md-6">
                        <div className="mb-2">
                            <strong>Last Update:</strong>
                            <span className="ms-2 text-muted">
                                {formatDate(gitStatus.lastUpdate)}
                            </span>
                        </div>
                        
                        {gitStatus.lastCommit && (
                            <div className="mb-2">
                                <strong>Last Commit:</strong>
                                <div className="ms-2 small text-muted">
                                    {gitStatus.lastCommit}
                                </div>
                            </div>
                        )}
                        
                        {gitStatus.hasChanges !== undefined && (
                            <div className="mb-2">
                                <strong>Changes:</strong>
                                <span className={`ms-2 badge ${gitStatus.hasChanges ? 'bg-warning' : 'bg-success'}`}>
                                    {gitStatus.hasChanges ? 'Uncommitted Changes' : 'Clean'}
                                </span>
                            </div>
                        )}
                        
                        {gitStatus.repoPath && (
                            <div className="mb-2">
                                <strong>Path:</strong>
                                <span className="ms-2 text-muted small">{gitStatus.repoPath}</span>
                            </div>
                        )}
                    </div>
                </div>
                
                {gitStatus.error && (
                    <div className="mt-3 alert alert-danger">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>Error:</strong> {gitStatus.error}
                    </div>
                )}
                
                <div className="mt-3 small text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Repository is automatically updated every 5 minutes. You can also update manually using the button above.
                </div>
            </div>
        </div>
    );
};

export default GitStatus; 